/* src/components/FormRenderer.jsx
 * Reusable form renderer that supports all layout types
 * Validates required fields before submit/next and shows pop-up with fix instructions.
 */

import React, { useState } from 'react';
import { getRowSectionLabel } from '../utils/formTemplates';
import { buildConsentText, parseConsentTextWithLinks } from '../utils/smsConsent';

// Placeholders that look like sample/fake data - show nothing so users enter real info
const SAMPLE_PLACEHOLDERS = [
  'john doe', 'jon doe', 'jdoe', 'john', 'doe', 'john doe', 'jane doe',
  'john@example.com', 'john@company.com', 'jdoe@me.com', 'your@email.com',
  'jon@example.com', 'example@example.com',
  '(555) 123-4567', '555-123-4567', 'acme inc.', 'acme',
  'tell us how we can help...', 'tell us about your project...',
  'share your thoughts...', 'any special accommodations needed?',
  'what did you like? what could be improved?',
];

function isSamplePlaceholder(value) {
  if (!value || typeof value !== 'string') return true;
  const normalized = value.trim().toLowerCase();
  return SAMPLE_PLACEHOLDERS.some(p => normalized === p || normalized.includes(p));
}

function getEffectivePlaceholder(field) {
  const p = field.placeholder;
  if (!p) return '';
  return isSamplePlaceholder(p) ? '' : p;
}

export default function FormRenderer({ 
  fields, 
  theme, 
  layout = 'modern', 
  layoutConfig = null,
  rows = null, // optional: [{ id, fieldIds }] for row-based layout
  fieldDirection = 'horizontal', // 'horizontal' = fields across, 'vertical' = fields stacked
  formName = 'Form',
  formData = {},
  onFieldChange = () => {},
  onSubmit = () => {},
  readOnly = false,
  submitting = false,
  smsOptIn = null,
  consentBusinessName = '',
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});
  const [validationPopup, setValidationPopup] = useState(null);

  // Get layout config
  const config = layoutConfig || { styles: { layout: 'single-column' } };
  const layoutType = config.styles?.layout || 'single-column';

  // Calculate steps for multi-step (divide fields into steps)
  const getSteps = () => {
    if (layoutType !== 'multi-step') return [];
    if (fields.length === 0) return [];
    const numSteps = config.styles?.steps || 3;
    const stepsPerGroup = Math.max(1, Math.ceil(fields.length / numSteps));
    const steps = [];
    for (let i = 0; i < fields.length; i += stepsPerGroup) {
      steps.push(fields.slice(i, i + stepsPerGroup));
    }
    return steps;
  };

  const steps = getSteps();
  const totalSteps = steps.length > 0 ? steps.length : 1;
  const currentStepFields = steps.length > 0 && steps[currentStep] ? steps[currentStep] : fields;

  // Validation: required empty or number out of range. Returns list of { field, label, message }.
  function getValidationErrors(fieldsToCheck) {
    const errors = [];
    for (const field of fieldsToCheck || []) {
      const value = formData[field.name];
      const trimmed = typeof value === 'string' ? value.trim() : value;
      const isEmpty = trimmed === '' || trimmed == null || (Array.isArray(trimmed) && trimmed.length === 0);
      if (field.required && isEmpty) {
        errors.push({
          field,
          label: field.label || field.name || 'This field',
          message: `Please enter ${(field.label || field.name).toLowerCase()}.`,
        });
        continue;
      }
      if (field.type === 'number' && trimmed !== '' && trimmed != null) {
        const num = Number(trimmed);
        if (!Number.isNaN(num)) {
          if (field.min != null && field.min !== '' && num < Number(field.min)) {
            errors.push({
              field,
              label: field.label || field.name,
              message: `Must be at least ${field.min}.`,
            });
          } else if (field.max != null && field.max !== '' && num > Number(field.max)) {
            errors.push({
              field,
              label: field.label || field.name,
              message: `Must be at most ${field.max}.`,
            });
          }
        }
      }
    }
    return errors;
  }

  function showValidationPopup(errors, forStep = false) {
    const title = 'Please complete the required information';
    const message = forStep
      ? 'To continue, please fill in the required fields on this step.'
      : 'To submit the form, please fill in all required fields.';
    const list = errors.map(e => `• ${e.label}: ${e.message}`).join('\n');
    const howToFix = 'Fill in each field marked with a red asterisk (*). You can then continue to the next step or submit the form.';
    setValidationPopup({ title, message, list, howToFix, invalidFields: errors.map(e => e.field.name) });
  }

  function renderSmsConsentBlock({ isFinalStep = true } = {}) {
    if (!smsOptIn?.enabled || !isFinalStep) return null;
    const consentText = buildConsentText({
      businessName: consentBusinessName,
      formName,
      template: smsOptIn.consentText,
    });
    const parts = parseConsentTextWithLinks(consentText);
    return (
      <div style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', color: theme.textColor, cursor: readOnly ? 'default' : 'pointer' }}>
          <input
            type="checkbox"
            name="__smsConsent"
            checked={!!formData.__smsConsent}
            onChange={(e) => onFieldChange('__smsConsent', e.target.checked)}
            disabled={readOnly}
            style={{ marginTop: '0.2rem', width: '18px', height: '18px', flexShrink: 0, accentColor: theme.primaryColor }}
          />
          <span style={{ fontSize: '0.9rem', lineHeight: 1.5 }}>
            {parts.map((part, idx) =>
              part.type === 'link' ? (
                <a
                  key={idx}
                  href={part.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: theme.primaryColor }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {part.value}
                </a>
              ) : (
                <span key={idx}>{part.value}</span>
              )
            )}
          </span>
        </label>
      </div>
    );
  }

  // Wrap submit: validate then call parent onSubmit
  function handleSubmitClick(e) {
    e.preventDefault();
    if (readOnly || submitting) return;
    const errors = getValidationErrors(fields);
    if (errors.length > 0) {
      showValidationPopup(errors, false);
      return;
    }
    onSubmit(e);
  }

  // Multi-step Next: validate given step fields (pass current step's fields from each layout branch)
  function handleNextClick(fieldsForStep = currentStepFields) {
    const errors = getValidationErrors(fieldsForStep);
    if (errors.length > 0) {
      showValidationPopup(errors, true);
      return;
    }
    setCurrentStep(Math.min(totalSteps - 1, currentStep + 1));
    setValidationPopup(null);
  }

  // Get field styling based on fieldStyle config
  const getFieldStyle = () => {
    const fieldStyle = config.styles?.fieldStyle || 'rounded';
    const baseStyle = {
      width: '100%',
      padding: '0.75rem',
      fontSize: '1rem',
      color: theme.textColor,
      backgroundColor: theme.backgroundColor,
      fontFamily: 'inherit', // picks up host font applied to the outer container
      boxSizing: 'border-box',
    };

    switch (fieldStyle) {
      case 'minimal':
        return {
          ...baseStyle,
          border: 'none',
          borderBottom: `1px solid ${theme.borderColor}`,
          borderRadius: '0',
          background: 'transparent',
        };
      case 'square':
        return {
          ...baseStyle,
          border: `1px solid ${theme.borderColor}`,
          borderRadius: '0',
        };
      case 'card':
        return {
          ...baseStyle,
          border: `1px solid ${theme.borderColor}`,
          borderRadius: theme.borderRadius,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        };
      default: // 'rounded'
        return {
          ...baseStyle,
          border: `1px solid ${theme.borderColor}`,
          borderRadius: theme.borderRadius,
        };
    }
  };

  // Get button styling based on buttonStyle config
  const getButtonStyle = () => {
    const buttonStyle = config.styles?.buttonStyle || 'rounded';
    const baseStyle = {
      padding: '0.75rem',
      border: 'none',
      cursor: (submitting || readOnly) ? 'not-allowed' : 'pointer',
      fontSize: '1rem',
      fontWeight: '500',
      marginTop: '1rem',
    };

    switch (buttonStyle) {
      case 'minimal':
        return {
          ...baseStyle,
          background: 'transparent',
          color: theme.buttonColor,
          border: `1px solid ${theme.buttonColor}`,
          borderRadius: '0',
        };
      case 'square':
        return {
          ...baseStyle,
          background: (submitting || readOnly) ? '#6c757d' : theme.buttonColor,
          color: theme.buttonTextColor,
          borderRadius: '0',
        };
      case 'rounded-full':
        return {
          ...baseStyle,
          background: (submitting || readOnly) ? '#6c757d' : theme.buttonColor,
          color: theme.buttonTextColor,
          borderRadius: '9999px',
        };
      default: // 'rounded'
        return {
          ...baseStyle,
          background: (submitting || readOnly) ? '#6c757d' : theme.buttonColor,
          color: theme.buttonTextColor,
          borderRadius: theme.borderRadius,
        };
    }
  };

  // Get spacing based on spacing config
  const getSpacing = () => {
    const spacing = config.styles?.spacing || 'comfortable';
    switch (spacing) {
      case 'compact':
        return '0.5rem';
      case 'standard':
        return '1rem';
      default: // 'comfortable'
        return '1.5rem';
    }
  };

  // Render a single field with proper styling
  function renderField(field, useCardStyle = false) {
    const value = formData[field.name] || '';
    const fieldStyleObj = getFieldStyle();

    const renderInput = () => {
      switch (field.type) {
        case 'textarea':
          return (
            <textarea
              name={field.name}
              value={value}
              onChange={(e) => onFieldChange(field.name, e.target.value)}
              required={field.required}
              placeholder={getEffectivePlaceholder(field)}
              disabled={readOnly}
              style={{
                ...fieldStyleObj,
                fontFamily: 'inherit',
                minHeight: '100px',
                resize: 'vertical',
              }}
            />
          );

        case 'select':
          return (
            <select
              name={field.name}
              value={value}
              onChange={(e) => onFieldChange(field.name, e.target.value)}
              required={field.required}
              disabled={readOnly}
              style={fieldStyleObj}
            >
              <option value="">Select an option</option>
              {field.options?.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
          );

        case 'radio':
          return (
            <div>
              {field.options?.map((option, idx) => (
                <label key={idx} style={{ display: 'block', marginBottom: '0.5rem', color: theme.textColor }}>
                  <input
                    type="radio"
                    name={field.name}
                    value={option}
                    checked={value === option}
                    onChange={(e) => onFieldChange(field.name, e.target.value)}
                    required={field.required}
                    disabled={readOnly}
                    style={{ marginRight: '0.5rem', accentColor: theme.primaryColor }}
                  />
                  {option}
                </label>
              ))}
            </div>
          );

        case 'checkbox':
          return (
            <div>
              {field.options?.map((option, idx) => (
                <label key={idx} style={{ display: 'block', marginBottom: '0.5rem', color: theme.textColor }}>
                  <input
                    type="checkbox"
                    name={`${field.name}_${idx}`}
                    value={option}
                    checked={formData[`${field.name}_${idx}`] === option}
                    onChange={(e) => onFieldChange(`${field.name}_${idx}`, e.target.checked ? option : '')}
                    disabled={readOnly}
                    style={{ marginRight: '0.5rem', accentColor: theme.primaryColor }}
                  />
                  {option}
                </label>
              ))}
            </div>
          );

        default: {
          const isNumber = field.type === 'number';
          const numMin = isNumber && field.min != null && field.min !== '' ? Number(field.min) : undefined;
          const numMax = isNumber && field.max != null && field.max !== '' ? Number(field.max) : undefined;
          // Allow free typing; only enforce min/max on blur and show a validation message when invalid
          const handleChange = (e) => {
            onFieldChange(field.name, e.target.value);
          };
          const handleBlur = (e) => {
            if (!isNumber || (numMin == null && numMax == null)) return;
            const next = e.target.value;
            const parsed = parseFloat(next);
            if (next === '' || Number.isNaN(parsed)) return;
            let clamped = parsed;
            if (numMin != null && parsed < numMin) clamped = numMin;
            else if (numMax != null && parsed > numMax) clamped = numMax;
            if (clamped !== parsed) onFieldChange(field.name, String(clamped));
          };
          const numValue = isNumber && value !== '' ? parseFloat(value) : NaN;
          const isInvalidNum = isNumber && !Number.isNaN(numValue) && (
            (numMin != null && numValue < numMin) || (numMax != null && numValue > numMax)
          );
          const validationMsg = isInvalidNum
            ? (numMin != null && numMax != null
                ? `Must be between ${numMin} and ${numMax}`
                : numMin != null
                  ? `Must be at least ${numMin}`
                  : `Must be at most ${numMax}`)
            : null;
          return (
            <div style={{ width: '100%' }}>
              <input
                type={field.type === 'phone' ? 'tel' : field.type}
                name={field.name}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                required={field.required}
                placeholder={getEffectivePlaceholder(field)}
                disabled={readOnly}
                min={numMin}
                max={numMax}
                style={{ ...fieldStyleObj, ...(isInvalidNum ? { borderColor: '#dc3545' } : {}) }}
                title={isNumber && (numMin != null || numMax != null) ? `Allowed: ${numMin != null ? numMin : 'no min'} to ${numMax != null ? numMax : 'no max'}` : undefined}
              />
              {validationMsg && (
                <div style={{ fontSize: '0.85rem', color: '#dc3545', marginTop: '0.25rem' }}>{validationMsg}</div>
              )}
            </div>
          );
        }
      }
    };

    if (useCardStyle) {
      return (
        <div key={field.id} style={{
          background: theme.backgroundColor,
          border: `1px solid ${theme.borderColor}`,
          borderRadius: theme.borderRadius,
          padding: '1rem',
          marginBottom: getSpacing(),
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem', 
            color: theme.textColor,
            fontWeight: '600',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {field.label || 'Field Label'}
            {field.required && <span style={{ color: '#dc3545' }}> *</span>}
          </label>
          {renderInput()}
        </div>
      );
    }

    return renderInput();
  }

  // Builder rows: resolve field ids to field objects
  const fieldById = Object.fromEntries((fields || []).map(f => [f.id, f]));
  const hasRows = rows && rows.length > 0;
  const getRowFields = (row) => (row.fieldIds || []).map(id => fieldById[id]).filter(Boolean);

  // Prevent Enter in textarea from submitting form (so user can add new lines in comment/message fields)
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && e.target?.tagName === 'TEXTAREA') e.stopPropagation();
  };

  const validationModal = validationPopup && (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="validation-popup-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem',
      }}
      onClick={() => setValidationPopup(null)}
    >
      <div
        style={{
          background: theme.backgroundColor,
          color: theme.textColor,
          borderRadius: theme.borderRadius,
          padding: '1.5rem',
          maxWidth: '420px',
          width: '100%',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="validation-popup-title" style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1.15rem' }}>
          {validationPopup.title}
        </h3>
        <p style={{ marginBottom: '0.75rem', fontSize: '0.95rem' }}>{validationPopup.message}</p>
        <pre style={{
          background: '#f5f5f5',
          padding: '0.75rem',
          borderRadius: '4px',
          fontSize: '0.85rem',
          whiteSpace: 'pre-wrap',
          marginBottom: '0.75rem',
        }}>
          {validationPopup.list}
        </pre>
        <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }}>
          <strong>How to fix:</strong> {validationPopup.howToFix}
        </p>
        <button
          type="button"
          onClick={() => setValidationPopup(null)}
          style={{
            padding: '0.6rem 1.2rem',
            background: theme.buttonColor,
            color: theme.buttonTextColor,
            border: 'none',
            borderRadius: theme.borderRadius,
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          OK
        </button>
      </div>
    </div>
  );

  // Multi-step with rows as steps (each row = one step)
  if (hasRows && layoutType === 'multi-step') {
    const rowSteps = rows.map(r => getRowFields(r));
    const totalStepsRows = Math.max(1, rowSteps.length);
    const currentStepFieldsRows = rowSteps[currentStep] || rowSteps[0] || [];
    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {config.styles?.showProgress && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              {rowSteps.map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    height: '4px',
                    background: idx <= currentStep ? theme.primaryColor : '#e0e0e0',
                    marginRight: idx < rowSteps.length - 1 ? '0.5rem' : 0,
                    borderRadius: '2px',
                    transition: 'all 0.3s',
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: theme.textColor }}>
              <span>Step {currentStep + 1} of {totalStepsRows}</span>
              <span>{Math.round(((currentStep + 1) / totalStepsRows) * 100)}% Complete</span>
            </div>
          </div>
        )}
        {rows[currentStep]?.title?.trim() && (
          <h3 style={{ margin: 0, color: theme.textColor, fontSize: '1.15rem', fontWeight: '600' }}>
            {rows[currentStep].title.trim()}
          </h3>
        )}
        <style>{`
          @media (max-width: 600px) { .form-renderer-step-row { grid-template-columns: 1fr !important; } }
        `}</style>
        <div className="form-renderer-step-row" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, currentStepFieldsRows.length)}, 1fr)`, gap: getSpacing() }}>
          {currentStepFieldsRows.map((field) => (
            <div key={field.id} style={{ minWidth: 0 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textColor, fontWeight: '500' }}>
                {field.label || 'Field Label'}
                {field.required && <span style={{ color: '#dc3545' }}> *</span>}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            style={{
              padding: '0.75rem 1.5rem',
              background: currentStep === 0 ? '#e0e0e0' : '#f0f0f0',
              color: currentStep === 0 ? '#999' : theme.textColor,
              border: 'none',
              borderRadius: theme.borderRadius,
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
            }}
          >
            ← Previous
          </button>
          {currentStep < totalStepsRows - 1 ? (
            <button
              type="button"
              onClick={() => handleNextClick(currentStepFieldsRows)}
              style={{
                padding: '0.75rem 1.5rem',
                background: theme.buttonColor,
                color: theme.buttonTextColor,
                border: 'none',
                borderRadius: theme.borderRadius,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
              }}
            >
              Next →
            </button>
          ) : (
            <>
              {renderSmsConsentBlock({ isFinalStep: true })}
              <button
              type="button"
              disabled={submitting || readOnly}
              onClick={handleSubmitClick}
              style={getButtonStyle()}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
            </>
          )}
        </div>
      </form>
      </>
    );
  }

  // Accordion with rows as sections (each row = one section)
  if (hasRows && layoutType === 'accordion') {
    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
        <style>{`
          @media (max-width: 600px) { .form-renderer-accordion-row { grid-template-columns: 1fr !important; } }
        `}</style>
        {rows.map((row, sectionIdx) => {
          const rowFields = getRowFields(row);
          const n = Math.max(1, rowFields.length);
          const sectionId = row.id || `section-${sectionIdx}`;
          return (
            <div
              key={sectionId}
              style={{
                border: `1px solid ${theme.borderColor}`,
                borderRadius: theme.borderRadius,
                overflow: 'hidden',
                marginBottom: getSpacing(),
              }}
            >
              <button
                type="button"
                onClick={() => setExpandedSections({ ...expandedSections, [sectionId]: !expandedSections[sectionId] })}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: expandedSections[sectionId] ? theme.primaryColor : '#f8f9fa',
                  color: expandedSections[sectionId] ? 'white' : theme.textColor,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{getRowSectionLabel(row, sectionIdx)}</span>
                <span>{expandedSections[sectionId] ? '▼' : '▶'}</span>
              </button>
              {expandedSections[sectionId] && (
                <div style={{ padding: '1rem', background: theme.backgroundColor, display: 'grid', gridTemplateColumns: `repeat(${n}, 1fr)`, gap: getSpacing() }} className="form-renderer-accordion-row">
                  {rowFields.map((field) => (
                    <div key={field.id} style={{ minWidth: 0 }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textColor, fontWeight: '500' }}>
                        {field.label || 'Field Label'}
                        {field.required && <span style={{ color: '#dc3545' }}> *</span>}
                      </label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {renderSmsConsentBlock()}
        <button type="button" onClick={handleSubmitClick} disabled={submitting || readOnly} style={getButtonStyle()}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
      </>
    );
  }

  // Tabbed with rows as tabs (each row = one tab)
  if (hasRows && layoutType === 'tabbed') {
    const tabFields = rows.map(r => getRowFields(r));
    const tabLabels = rows.map((row, idx) => getRowSectionLabel(row, idx));
    const activeTabFieldCount = Math.max(1, (tabFields[activeTab] || []).length);
    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
        <div style={{ display: 'flex', borderBottom: `2px solid ${theme.borderColor}` }}>
          {tabLabels.map((tab, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveTab(idx)}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === idx ? `3px solid ${theme.primaryColor}` : '3px solid transparent',
                color: activeTab === idx ? theme.primaryColor : theme.textColor,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: activeTab === idx ? '600' : '500',
              }}
            >
              {tab}
            </button>
          ))}
        </div>
        <style>{`
          @media (max-width: 600px) { .form-renderer-tab-row { grid-template-columns: 1fr !important; } }
        `}</style>
        <div className="form-renderer-tab-row" style={{ minHeight: '200px', display: 'grid', gridTemplateColumns: 'repeat(' + activeTabFieldCount + ', 1fr)', gap: getSpacing() }}>
          {(tabFields[activeTab] || []).map((field) => (
            <div key={field.id} style={{ minWidth: 0 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textColor, fontWeight: '500' }}>
                {field.label || 'Field Label'}
                {field.required && <span style={{ color: '#dc3545' }}> *</span>}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>
        {renderSmsConsentBlock()}
        <button type="button" onClick={handleSubmitClick} disabled={submitting || readOnly} style={{ ...getButtonStyle(), width: '100%' }}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
      </>
    );
  }

  // Row-based layout (horizontal strips) when rows present and layout is single-column, two-column, card, etc.
  if (hasRows) {
    const isVertical = fieldDirection === 'vertical';
    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
        <style>{`
          .form-renderer-row { gap: ${getSpacing()}; }
          @media (max-width: 600px) { .form-renderer-row { grid-template-columns: 1fr !important; flex-direction: column !important; } }
        `}</style>
        {rows.map((row) => {
          const rowFields = getRowFields(row);
          const n = Math.max(1, rowFields.length);
          return (
            <div
              key={row.id}
              className="form-renderer-row"
              style={{
                display: isVertical ? 'flex' : 'grid',
                flexDirection: isVertical ? 'column' : undefined,
                gridTemplateColumns: !isVertical ? `repeat(${n}, 1fr)` : undefined,
                gap: getSpacing(),
              }}
            >
              {rowFields.map((field) => (
                <div key={field.id} style={{ minWidth: 0 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textColor, fontWeight: '500' }}>
                    {field.label || 'Field Label'}
                    {field.required && <span style={{ color: '#dc3545' }}> *</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}
            </div>
          );
        })}
        {renderSmsConsentBlock()}
        <button type="button" onClick={handleSubmitClick} disabled={submitting || readOnly} style={{ ...getButtonStyle(), width: '100%' }}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
      </>
    );
  }

  // Multi-step layout (no rows: divide fields by step count)
  if (layoutType === 'multi-step') {
    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Progress Indicator */}
        {config.styles?.showProgress && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  style={{
                    flex: 1,
                    height: '4px',
                    background: idx <= currentStep ? theme.primaryColor : '#e0e0e0',
                    marginRight: idx < steps.length - 1 ? '0.5rem' : 0,
                    borderRadius: '2px',
                    transition: 'all 0.3s',
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: theme.textColor }}>
              <span>Step {currentStep + 1} of {totalSteps}</span>
              <span>{Math.round(((currentStep + 1) / totalSteps) * 100)}% Complete</span>
            </div>
          </div>
        )}

        {/* Current Step Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
          {currentStepFields.map((field) => (
            <div key={field.id}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                color: theme.textColor,
                fontWeight: '500',
              }}>
                {field.label || 'Field Label'}
                {field.required && <span style={{ color: '#dc3545' }}> *</span>}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
            style={{
              padding: '0.75rem 1.5rem',
              background: currentStep === 0 ? '#e0e0e0' : '#f0f0f0',
              color: currentStep === 0 ? '#999' : theme.textColor,
              border: 'none',
              borderRadius: theme.borderRadius,
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
            }}
          >
            ← Previous
          </button>
          {currentStep < totalSteps - 1 ? (
            <button
              type="button"
              onClick={() => handleNextClick()}
              style={{
                padding: '0.75rem 1.5rem',
                background: theme.buttonColor,
                color: theme.buttonTextColor,
                border: 'none',
                borderRadius: theme.borderRadius,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
              }}
            >
              Next →
            </button>
          ) : (
            <>
              {renderSmsConsentBlock({ isFinalStep: true })}
              <button
              type="button"
              disabled={submitting || readOnly}
              onClick={handleSubmitClick}
              style={getButtonStyle()}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
            </>
          )}
        </div>
      </form>
      </>
    );
  }

  // Accordion layout
  if (layoutType === 'accordion') {
    // Group fields into sections (3 fields per section by default)
    const fieldsPerSection = Math.max(1, Math.ceil(fields.length / 3));
    const sections = [];
    for (let i = 0; i < fields.length; i += fieldsPerSection) {
      sections.push({
        id: `section-${i}`,
        title: `Section ${Math.floor(i / fieldsPerSection) + 1}`,
        fields: fields.slice(i, i + fieldsPerSection),
      });
    }

    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
        {sections.map((section, sectionIdx) => (
          <div
            key={section.id}
            style={{
              border: `1px solid ${theme.borderColor}`,
              borderRadius: theme.borderRadius,
              overflow: 'hidden',
              marginBottom: getSpacing(),
            }}
          >
            <button
              type="button"
              onClick={() => setExpandedSections({
                ...expandedSections,
                [section.id]: !expandedSections[section.id],
              })}
              style={{
                width: '100%',
                padding: '1rem',
                background: expandedSections[section.id] ? theme.primaryColor : '#f8f9fa',
                color: expandedSections[section.id] ? 'white' : theme.textColor,
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{section.title}</span>
              <span>{expandedSections[section.id] ? '▼' : '▶'}</span>
            </button>
            {expandedSections[section.id] && (
              <div style={{ padding: '1rem', background: theme.backgroundColor }}>
                {section.fields.map((field) => (
                  <div key={field.id} style={{ marginBottom: getSpacing() }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      color: theme.textColor,
                      fontWeight: '500',
                    }}>
                      {field.label || 'Field Label'}
                      {field.required && <span style={{ color: '#dc3545' }}> *</span>}
                    </label>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {renderSmsConsentBlock()}
        <button
          type="button"
          onClick={handleSubmitClick}
          disabled={submitting || readOnly}
          style={getButtonStyle()}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
      </>
    );
  }

  // Tabbed layout
  if (layoutType === 'tabbed') {
    const tabs = config.styles?.tabs || ['Tab 1', 'Tab 2', 'Tab 3'];
    const fieldsPerTab = Math.ceil(fields.length / tabs.length);
    const tabFields = tabs.map((_, idx) => 
      fields.slice(idx * fieldsPerTab, (idx + 1) * fieldsPerTab)
    );

    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `2px solid ${theme.borderColor}` }}>
          {tabs.map((tab, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveTab(idx)}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === idx ? `3px solid ${theme.primaryColor}` : '3px solid transparent',
                color: activeTab === idx ? theme.primaryColor : theme.textColor,
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: activeTab === idx ? '600' : '500',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ minHeight: '200px' }}>
          {tabFields[activeTab]?.map((field) => (
            <div key={field.id} style={{ marginBottom: getSpacing() }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                color: theme.textColor,
                fontWeight: '500',
              }}>
                {field.label || 'Field Label'}
                {field.required && <span style={{ color: '#dc3545' }}> *</span>}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>

        {/* Submit Button */}
        {activeTab === tabs.length - 1 && (
          <>
            {renderSmsConsentBlock({ isFinalStep: true })}
            <button
            type="button"
            onClick={handleSubmitClick}
            disabled={submitting || readOnly}
            style={{ ...getButtonStyle(), width: '100%' }}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
          </>
        )}
      </form>
      </>
    );
  }

  // Card layout
  if (layoutType === 'card') {
    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
          {fields.map((field) => renderField(field, true))}
          {renderSmsConsentBlock()}
          <button
            type="button"
            onClick={handleSubmitClick}
            disabled={submitting || readOnly}
            style={{ ...getButtonStyle(), width: '100%' }}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </>
    );
  }

  // Two-column layout
  if (layoutType === 'two-column') {
    return (
      <>
        {validationModal}
        <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
        <style>{`
          .two-column-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: ${getSpacing()};
          }
          @media (max-width: 768px) {
            .two-column-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
        <div className="two-column-grid">
          {fields.map((field) => (
            <div key={field.id}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                color: theme.textColor,
                fontWeight: '500',
              }}>
                {field.label || 'Field Label'}
                {field.required && <span style={{ color: '#dc3545' }}> *</span>}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>
        {renderSmsConsentBlock()}
        <button
          type="button"
          onClick={handleSubmitClick}
          disabled={submitting || readOnly}
          style={{ ...getButtonStyle(), width: '100%' }}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
      </>
    );
  }

  // Default single-column layout (backward compat when no rows)
  return (
    <>
      {validationModal}
      <form onSubmit={handleSubmitClick} onKeyDown={handleFormKeyDown} style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: getSpacing() }}>
          {fields.map((field) => (
            <div key={field.id}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.textColor, fontWeight: '500' }}>
                {field.label || 'Field Label'}
                {field.required && <span style={{ color: '#dc3545' }}> *</span>}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>
        {renderSmsConsentBlock()}
        <button type="button" onClick={handleSubmitClick} disabled={submitting || readOnly} style={{ ...getButtonStyle(), width: '100%' }}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </>
  );
}
